/**
 * GTM Events
 * Send GTM tracking events
 */

/**
 * Build search signature for deduplication
 * Normalized signature to detect when search parameters change
 * @param {URLSearchParams} params
 * @returns {string}
 */
export function buildSearchSignature(params) {
  const normList = (val) =>
    val
      ? val
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
          .sort()
          .join(',')
      : '';

  const sig = {
    q: params.get('q') || '',
    sportSlug: normList(params.get('sportSlug') || ''),
    types: normList(params.get('types') || ''),
    gender: normList(params.get('gender') || ''),
    age: params.get('age') || '',
    beginDates: params.get('beginDates') || '',
    endDates: params.get('endDates') || '',
  };

  return JSON.stringify(sig);
}

/**
 * Get search session ID (regenerates if signature changed)
 * @param {URLSearchParams} params
 * @returns {string}
 */
export function getSearchSessionId(params) {
  const ID_KEY = 'camps_search_session_id';
  const SIG_KEY = 'last_search_signature';
  const signature = buildSearchSignature(params);

  try {
    const lastSig = localStorage.getItem(SIG_KEY);
    let sessionId = localStorage.getItem(ID_KEY);

    const needNew = !sessionId || !lastSig || lastSig !== signature;
    if (needNew) {
      sessionId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
        /[xy]/g,
        function (c) {
          const r = (Math.random() * 16) | 0;
          const v = c === 'x' ? r : (r & 0x3) | 0x8;
          return v.toString(16);
        }
      );
      localStorage.setItem(ID_KEY, sessionId);
      localStorage.setItem(SIG_KEY, signature);
    }

    return sessionId || 'unknown';
  } catch (e) {
    console.warn('Failed to get/set search session ID:', e);
    return 'unknown';
  }
}

/**
 * Send GTM event for search results view
 * Only fires on results page with search parameters
 * Prevents duplicate events for same search_session_id
 */
export function sendSearchResultsEventOnPageLoad() {
  // Only send if we're on the results page and have search parameters
  if (!window.location.pathname.includes('/search/results')) {
    return;
  }

  const params = new URLSearchParams(window.location.search);
  if (!params.toString()) {
    return; // No search parameters
  }

  // Initialize dataLayer
  window.dataLayer = window.dataLayer || [];

  const searchSessionId = getSearchSessionId(params);

  // Prevent duplicate push if same search_session_id already sent
  try {
    const LAST_KEY = 'last_view_search_results_session_id';
    const lastSent = localStorage.getItem(LAST_KEY);
    if (lastSent && lastSent === searchSessionId) {
      console.log(
        'ℹ️ [Results Page] Skip dataLayer push: same search_session_id',
        searchSessionId
      );
      return;
    }
  } catch (e) {
    // ignore storage errors
  }

  // Get location
  const searchLocation = params.get('q') || '';

  // Get sports (map slugs to display names)
  const sportSlugParam = params.get('sportSlug') || '';
  const sportSlugs = sportSlugParam
    ? sportSlugParam.split(',').map((s) => s.trim())
    : [];
  const sportItems = Array.from(
    document.querySelectorAll('.rd-search-modal__sport-item')
  );
  const searchSport = sportSlugs
    .map((slug) => {
      const item = sportItems.find((item) => item.dataset.sportSlug === slug);
      return item ? item.textContent.trim() : slug;
    })
    .filter(Boolean);

  // Get dates type
  const beginDates = params.get('beginDates') || '';
  const endDates = params.get('endDates') || '';
  let storedDatesState = null;
  try {
    storedDatesState = JSON.parse(
      localStorage.getItem('dates-selection-state') || 'null'
    );
  } catch (e) {
    storedDatesState = null;
  }

  function getDatesType(beginDates, endDates) {
    if (!beginDates || !endDates) {
      return 'none';
    }
    // Try to get type from localStorage snapshot
    if (storedDatesState && storedDatesState.selectedType) {
      if (storedDatesState.selectedType === 'dateRange') return 'range';
      if (storedDatesState.selectedType === 'month') return 'month';
      if (storedDatesState.selectedType === 'season') return 'season';
    }
    // Fallback: check if dates are in YYYY-MM-DD format
    const dateRangePattern = /^\d{4}-\d{2}-\d{2}$/;
    if (
      dateRangePattern.test(beginDates) &&
      dateRangePattern.test(endDates)
    ) {
      return 'range';
    }
    return 'none';
  }

  const searchDatesType = getDatesType(beginDates, endDates);

  // Get camper age group
  const age = params.get('age') || '';
  const camperAgeGroup = age || undefined;

  // Get camper gender
  const gender = params.get('gender') || '';
  let camperGender = undefined;
  if (gender) {
    const genderLower = gender.toLowerCase();
    if (genderLower === 'male' || genderLower === 'm') {
      camperGender = 'male';
    } else if (genderLower === 'female' || genderLower === 'f') {
      camperGender = 'female';
    } else if (
      genderLower === 'coed' ||
      genderLower === 'co-ed' ||
      genderLower === 'x'
    ) {
      camperGender = 'coed';
    }
  }

  // Build event data
  const eventData = {
    event: 'view_search_results',
    search_session_id: searchSessionId,
    search_location: searchLocation || '',
    search_dates_type: searchDatesType,
  };

  // Add search_sport
  if (searchSport.length > 0) {
    eventData.search_sport =
      searchSport.length === 1 ? searchSport[0] : searchSport;
  }

  // Add dates value
  if (beginDates && endDates && searchDatesType !== 'none') {
    if (searchDatesType === 'range') {
      eventData.search_dates_value = `${beginDates}_${endDates}`;
    } else if (searchDatesType === 'month') {
      if (
        storedDatesState &&
        Array.isArray(storedDatesState.selectedMonths) &&
        storedDatesState.selectedMonths.length > 0
      ) {
        eventData.search_dates_value = storedDatesState.selectedMonths
          .map((m) => m.displayText || `${m.month}_${m.year}`)
          .join(', ');
      } else {
        eventData.search_dates_value = beginDates;
      }
    } else if (searchDatesType === 'season') {
      if (
        storedDatesState &&
        Array.isArray(storedDatesState.selectedSeasons) &&
        storedDatesState.selectedSeasons.length > 0
      ) {
        eventData.search_dates_value = storedDatesState.selectedSeasons
          .map((s) => s.displayText || `${s.season}_${s.year}`)
          .join(', ');
      } else {
        eventData.search_dates_value = beginDates;
      }
    }
  }

  if (camperAgeGroup) {
    eventData.camper_age_group = camperAgeGroup;
  }

  if (camperGender) {
    eventData.camper_gender = camperGender;
  }

  // Debug logging
  console.log(
    '🔍 [Results Page] Sending view_search_results event:',
    eventData
  );
  console.log(
    '📊 Current dataLayer length:',
    window.dataLayer ? window.dataLayer.length : 0
  );

  // Push to dataLayer
  window.dataLayer.push(eventData);

  localStorage.setItem(
    'last_view_search_results_event_data',
    JSON.stringify(eventData)
  );

  // Persist last sent session id to avoid duplicate pushes
  try {
    localStorage.setItem('last_view_search_results_session_id', searchSessionId);
  } catch (e) {
    // ignore storage errors
  }

  // Verify push
  console.log(
    '✅ [Results Page] Event pushed to dataLayer. New length:',
    window.dataLayer.length
  );
  console.log(
    '📋 Last event in dataLayer:',
    window.dataLayer[window.dataLayer.length - 1]
  );
}

console.log('📦 GTM events module loaded');
